import { useLocale, useTranslate } from '../../application/i18n/LocaleProvider';
import { carName } from '../../domain/cars/carCatalogue';
import {
    EM_DASH,
    formatSpecNumber,
    specLabelKey,
    specUnitKey,
} from '../../domain/cars/carProperties';
import { specFractionDigits, specHasUnit, specValue } from '../../domain/cars/carFigures';
import { CARD_VALUES } from '../../domain/game/overtakeCards';
import { artSource } from '../cars/carArt';

/**
 * One card of the Overtake — a printed object, not a plate (SPEC-011 PR-11.5).
 *
 * The card is the one LIGHT surface in this product and the reasoning is in
 * `tokens.css` beside the paper: this is the physical thing the catalogue is a
 * deck of, and a dark card on a dark table is a panel rather than something you
 * could pick up. Everything below is drawn from the Hungarian quartett cards
 * these figures were first read off — an origin plate, the marque over the
 * model, the artwork, the block of figures every card carries, and the one
 * figure the round is being played for called out under a rule.
 *
 * IT ALWAYS SHOWS ITS BLOCK. That is the rule the original cards teach and the
 * first version of this screen got wrong: you choose which figure to call by
 * reading your own hand, so a card that hid its numbers until after the call
 * made leading a guess. The figure IN PLAY is additional to the block, not
 * instead of it — it is set large under a rule, because it is the only number
 * that decides anything this round.
 *
 * The counterpart of `DuelOption`, and still a separate component for the same
 * reason: a duel card may never show a figure, because guessing which is bigger
 * IS the duel. Two opposite rules about one picture is two components.
 *
 * `aria-disabled` rather than `disabled`, for the reason `DuelOption` gives: a
 * disabled element drops focus on the spot in several engines, so a keyboard
 * reader choosing a card would lose their place in their own hand.
 */

/**
 * The four figures every card prints, in the order the originals print them.
 *
 * `Hengerűrtartalom`, `Teljesítmény`, `Legnagyobb sebesség`, `Gyorsulás` —
 * displacement, power, top speed and the run to a hundred. The fifth the
 * originals carry is fuel consumption, and the catalogue does not have it
 * (SPEC-002 would be where it landed).
 */
const CARD_FIGURES = ['displacement', 'power', 'topSpeed', 'zeroToHundred'];

/**
 * The marque over the model, split off the canonical name rather than rebuilt
 * from `make` and `model`.
 *
 * Six of the twenty-four records carry an authored name that is NOT `make` plus
 * `model` — "Porsche 911 (993)" against "Porsche 911 Carrera (993)" — and
 * `carName` is the one the rest of the product uses. Recomposing here would put
 * a different name on the card than on the car's own screen, and would quietly
 * break every test that finds a card by the name the catalogue gives it.
 *
 * So the canonical name is SPLIT: the marque comes off the front when it is
 * there, and the two spans still read as one name to anything concatenating
 * them.
 */
/**
 * How much of a step down this model's length asks for.
 *
 * Two steps and then clipping. The thresholds are counted off the catalogue's
 * own names rather than guessed: "512 TR" and "F40" want none, "Esprit Turbo"
 * and "CTR Yellowbird" want one, "Corvette ZR-1 (C4)" and "Skyline GT-R (R34)"
 * want two, and "Countach 25th Anniversary" is longer than any card is wide.
 */
function modelFit(model) {
    const length = String(model ?? '').length;

    if (length > 16) {
        return ' overtake-card__model--tiny';
    }

    return length > 11 ? ' overtake-card__model--small' : '';
}

function splitName(car) {
    const name = carName(car);
    const make = String(car?.make ?? '');

    return make && name.startsWith(`${make} `)
        ? { make, model: name.slice(make.length + 1) }
        : { make: null, model: name };
}

/**
 * The take a CARD is cut from: the SIDE PROFILE, the same one the catalogue is
 * drawn from.
 *
 * It was the front three-quarter, on the argument that a portrait frame wants a
 * portrait view — and the frame stopped being portrait. A card in the hand is
 * one of five across a phone and the one on the table shares its height with the
 * hand below it, so the artwork's box is now WIDE and short, which is the shape
 * a side profile is drawn in and the shape a front view has to be letterboxed
 * into.
 *
 * The side profile is also the view a car is a SILHOUETTE in, which is what the
 * next thing this card is meant to do depends on: a window that rolls down is a
 * gesture you can only read from the side.
 *
 * Still its own ladder rather than a call to `catalogueTake` — a card falls
 * through until the record can honour one, and the two screens are allowed to
 * disagree about which take they prefer.
 */
const CARD_TAKES = ['side', 'front', 'top-front', 'top-front2'];

function cardTake(car) {
    const takes = car?.media?.takes ?? [];

    return CARD_TAKES.find((take) => takes.includes(take)) ?? null;
}

export default function OvertakeCard({
    car,
    spec,
    isPlayable = true,
    isSelected = false,
    isStanding = false,
    onSelect,
    size = 'hand',
    fanTurn = null,
}) {
    const t = useTranslate();
    const { languageCode } = useLocale();
    const art = artSource(car, cardTake(car));
    const { make, model } = splitName(car);
    const called = spec ? specValue(car, spec) : null;
    const className = [
        'overtake-card',
        `overtake-card--${size}`,
        isSelected ? 'is-selected' : '',
        isStanding ? 'is-standing' : '',
        onSelect && !isPlayable ? 'is-unplayable' : '',
    ].filter(Boolean).join(' ');
    const style = {
        // What this card is WORTH, as a length. The band across the card's top
        // edge is as many segments as the card pays into a pot, so rarity is
        // encoded by size as well as by hue and a reader who cannot separate
        // amber from grey can still count it (the accessibility rule about
        // colour never being the only carrier).
        '--card-worth': CARD_VALUES[car?.rarity] ?? CARD_VALUES.common,
        ...(fanTurn === null ? {} : { '--fan-turn': fanTurn }),
    };

    const inside = (
        <>
            <span className={`overtake-card__band overtake-card__band--${car.rarity}`} aria-hidden="true" />

            <span className="overtake-card__head">
                <span className="overtake-card__origin">{t(`cars.origin.${car.origin}.plate`)}</span>
                {/* The explicit space is load-bearing. JSX drops whitespace
                    that spans a newline, so without it these two concatenate to
                    "Porsche911 (993)" — which is the control's accessible name,
                    and is what every test that finds a card by the catalogue's
                    own name for it matches against. */}
                <span className="overtake-card__title">
                    {make ? <span className="overtake-card__make">{make}</span> : null}
                    {make ? ' ' : null}
                    {/* ONE LINE, ALWAYS. A name that wrapped moved everything
                        under it — the picture, the figure, the card's own height
                        in a band that is sharing the fold — so the model is set
                        on a single line and the LENGTH of it steps the size
                        down. The component measures because CSS cannot: this is
                        the same trick the duel's gauge takes for a wide pairing
                        (SPEC-007 D-67), and it is what keeps "CTR Yellowbird" a
                        whole name instead of a clipped one. Past the last step a
                        name is still clipped rather than shrunk further, because
                        a name too small to read is not a name — and the whole of
                        it is in the card's accessible name either way. */}
                    <span className={`overtake-card__model${modelFit(model)}`}>{model}</span>
                </span>
            </span>

            {/* The worth, as a word, for anything that cannot see the band. */}
            <span className="visually-hidden">{t(`cars.rarity.${car.rarity}`)}</span>

            <span className="overtake-card__stage">
                {art ? (
                    <img
                        className="overtake-card__art"
                        src={art.src}
                        srcSet={art.srcSet}
                        /* Five fanned across a phone, one larger on the table.
                           Stated in CSS terms because the browser picks a rung
                           before the stylesheet has resolved. */
                        sizes="(min-width: 640px) 220px, 40vw"
                        /* The name is in the same control and a screen reader
                           would otherwise hear the car twice per card. */
                        alt=""
                        loading="lazy"
                        decoding="async"
                    />
                ) : null}
            </span>

            {/* The block, MINUS the figure in play. D-20's rule is that a card
                shows its numbers so you can choose which to call — and the one
                being played for is set large under the rule below, so printing
                it twice on one card spends a row on a number the reader is
                already looking at. */}
            <dl className="overtake-card__block">
                {CARD_FIGURES.filter((figure) => figure !== spec).map((figure) => (
                    <div className="overtake-card__row" key={figure}>
                        <dt>{t(specLabelKey(figure))}</dt>
                        <dd className="figure">
                            <CardFigure car={car} figure={figure} languageCode={languageCode} t={t} />
                        </dd>
                    </div>
                ))}
            </dl>

            {/* The figure the round is being played for. Under a rule and set
                large, because it is the only number on the card that decides
                anything — and drawn even where the card does not carry it, since
                an em dash says "cannot go here" and a gap says nothing. */}
            {spec ? (
                <span className="overtake-card__called">
                    <span className="overtake-card__called-label">{t(specLabelKey(spec))}</span>
                    <span className="overtake-card__called-value figure">
                        {called === null ? EM_DASH : formatSpecNumber(called, {
                            languageCode,
                            fractionDigits: specFractionDigits(spec),
                        })}
                    </span>
                    {/* `specHasUnit`, never `specUnitKey` — the key is COMPOSED
                        and therefore always truthy, so guarding on it guards on
                        nothing and prints `cars.spec.cylinders.unit` under the
                        number for the two figures that are counts. */}
                    {called !== null && specHasUnit(spec) ? (
                        <span className="overtake-card__called-unit">{t(specUnitKey(spec))}</span>
                    ) : null}
                </span>
            ) : null}
        </>
    );

    if (!onSelect) {
        return <div className={className} style={style}>{inside}</div>;
    }

    return (
        <button
            type="button"
            className={className}
            style={style}
            onClick={() => isPlayable && onSelect(car.id)}
            aria-disabled={isPlayable ? undefined : 'true'}
            aria-pressed={isSelected}
        >
            {inside}
        </button>
    );
}

/**
 * One value in the block.
 *
 * Power is the exception and it is the original card's own: it prints
 * `Teljesítmény (kW/LE)` as a PAIR, because the two numbers are the same fact in
 * the two units the period argued about. The catalogue carries both halves, so
 * the card prints both — and it is composed through a message rather than
 * concatenated here, so a translator can reorder the pair.
 */
function CardFigure({ car, figure, languageCode, t }) {
    const value = specValue(car, figure);
    const kilowatts = car?.specs?.power?.kw;

    if (figure === 'power' && Number.isFinite(kilowatts) && value !== null) {
        return (
            <>
                {t('play.card.power', {
                    kw: formatSpecNumber(kilowatts, { languageCode }),
                    hp: formatSpecNumber(value, { languageCode }),
                })}
                <span className="overtake-card__unit">{t('play.card.power.unit')}</span>
            </>
        );
    }

    if (value === null) {
        return EM_DASH;
    }

    return (
        <>
            {formatSpecNumber(value, { languageCode, fractionDigits: specFractionDigits(figure) })}
            {specHasUnit(figure) ? (
                <span className="overtake-card__unit">{t(specUnitKey(figure))}</span>
            ) : null}
        </>
    );
}
